import type { ReaderEvent } from '@shared/events';

import type { Destination } from '../destination';
import { urlOf } from '../lazy-sdk';
import { titleOf, toVendorEvent, type VendorMapping } from '../mapping';

declare global {
	interface Window {
		// The real gtag.js function: the command name first ('event',
		// 'config', ...), then command-specific arguments. Defined by
		// index-template.ejs, and absent for a reader who had analytics off
		// when the page loaded.
		gtag?: (command: string, ...args: unknown[]) => void;
	}
}

/**
 * Where Google Analytics cannot take a canonical name. `session_id` is a
 * reserved parameter name on web streams; Canvasflow's own session reaches
 * GA4 as `cf_session_id`. `screen_view` is sent as GA4's own `page_view` so
 * the property's standard reports keep working.
 *
 * `ad_impression` is reserved on web streams too (it is Google's own ad
 * event), so GA4 receives `cf_ad_impression`.
 *
 * `app_install` and `ad_click` are reserved on app streams and by the Measurement Protocol
 * only; gtag.js sends to a web stream, which accepts it, so it keeps its
 * name. A relay through the Measurement Protocol would have to rename it.
 */
export const GA4_MAPPING = {
	events: {
		screen_view: 'page_view',
		ad_impression: 'cf_ad_impression',
		// Google's own ecommerce names, so its funnel and conversion reports
		// pick them up. No value or currency is sent: the app doesn't know
		// what was paid.
		checkout_start: 'begin_checkout',
		subscribe: 'purchase',
	},
	params: { session_id: 'cf_session_id' },
} satisfies VendorMapping;

/**
 * Google Analytics 4 through gtag.js, on the tenant's own property.
 * index-template.ejs loads and configures gtag (with `send_page_view: false`,
 * so this is the only source of page views) before the app starts.
 */
export function createGa4Destination(trackerId: string): Destination {
	// Google's documented per-property opt-out: while it is true, gtag.js
	// sends nothing for this id, including anything the page queued itself.
	const setDisabled = (disabled: boolean) => {
		(window as unknown as Record<string, boolean>)[`ga-disable-${trackerId}`] =
			disabled;
	};

	return {
		name: 'ga4',
		start: () => setDisabled(false),
		stop: () => setDisabled(true),
		send: (event: ReaderEvent) => {
			if (!window.gtag) {
				return;
			}
			const { name, params } = toVendorEvent(event, GA4_MAPPING);
			if (event.name === 'screen_view') {
				params.page_location = urlOf(event.path);
				params.page_title = titleOf(event) ?? document.title;
			}
			window.gtag('event', name, params);
		},
	};
}
